import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const CohortGroupingPanel = ({ data, onCohortSelect }) => {
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [groupBy, setGroupBy] = useState('month'); // month, week, quarter
  const [filterBy, setFilterBy] = useState('all'); // all, converted, active, churned

  if (!data || data?.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
        No cohort data available
      </div>
    );
  }

  const handleCohortClick = (cohort) => {
    setSelectedCohort(cohort);
    onCohortSelect?.(cohort);
  };

  const getCohortHealth = (retention) => {
    if (retention >= 70) return { color: 'success', label: 'Excellent', icon: 'TrendingUp' };
    if (retention >= 50) return { color: 'warning', label: 'Good', icon: 'Minus' };
    return { color: 'error', label: 'At Risk', icon: 'TrendingDown' };
  };

  const getBehaviorBadge = (behavior) => {
    const badges = {
      'high-engagement': { color: 'bg-success/20 text-success', label: 'High Engagement' },
      'moderate-engagement': { color: 'bg-primary/20 text-primary', label: 'Moderate' },
      'low-engagement': { color: 'bg-warning/20 text-warning', label: 'Low Engagement' },
      'inactive': { color: 'bg-error/20 text-error', label: 'Inactive' }
    };
    return badges?.[behavior] || badges?.['moderate-engagement'];
  };

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Icon name="Filter" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Group by:</span>
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e?.target?.value)}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="week">Week</option>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filter:</span>
          <select
            value={filterBy}
            onChange={(e) => setFilterBy(e?.target?.value)}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Users</option>
            <option value="converted">Converted to Premium</option>
            <option value="active">Active Users</option>
            <option value="churned">Churned Users</option>
          </select>
        </div>
      </div>

      {/* Cohort Cards */}
      <div className="grid grid-cols-1 gap-4">
        {data?.map((cohort, index) => {
          const health = getCohortHealth(cohort?.currentRetention);
          const behaviorBadge = getBehaviorBadge(cohort?.behaviorPattern);
          const isSelected = selectedCohort?.cohortId === cohort?.cohortId;

          return (
            <div
              key={index}
              onClick={() => handleCohortClick(cohort)}
              className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                isSelected
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-foreground">{cohort?.cohortName}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${behaviorBadge?.color}`}>
                      {behaviorBadge?.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Acquired: {cohort?.acquisitionDate} • {cohort?.totalUsers?.toLocaleString()} users
                  </p>
                </div>
                <div className={`flex items-center gap-1 text-${health?.color}`}>
                  <Icon name={health?.icon} size={16} />
                  <span className="text-xs font-medium">{health?.label}</span>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Retention</p>
                  <p className="text-sm font-bold text-foreground">{cohort?.currentRetention}%</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Conversion</p>
                  <p className="text-sm font-bold text-success">{cohort?.conversionRate}%</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Avg LTV</p>
                  <p className="text-sm font-bold text-primary">£{cohort?.avgLTV}</p>
                </div>
                <div className="p-2 bg-background rounded">
                  <p className="text-xs text-muted-foreground mb-1">Churn</p>
                  <p className="text-sm font-bold text-error">{cohort?.churnRate}%</p>
                </div>
              </div>

              {/* Behavior Insights */}
              {cohort?.behaviorInsights && (
                <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Icon name="Lightbulb" size={14} className="text-primary mt-0.5" />
                    <p className="text-xs text-muted-foreground">{cohort?.behaviorInsights}</p>
                  </div>
                </div>
              )}

              {/* Premium Conversion Timeline */}
              {cohort?.avgConversionTime && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Clock" size={12} />
                  <span>Avg conversion time: {cohort?.avgConversionTime}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-4 p-4 bg-gradient-to-r from-primary/10 to-success/10 rounded-lg border border-primary/20">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Cohorts</p>
            <p className="text-xl font-bold text-foreground">{data?.length}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Total Users</p>
            <p className="text-xl font-bold text-foreground">
              {data?.reduce((sum, c) => sum + c?.totalUsers, 0)?.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Avg Retention</p>
            <p className="text-xl font-bold text-success">
              {(data?.reduce((sum, c) => sum + c?.currentRetention, 0) / data?.length)?.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CohortGroupingPanel;